/* eslint-disable @typescript-eslint/no-unused-vars */
import { FarmIcon } from '@assets/icons/farm';
import { ServicesCongratsIcon } from '@assets/icons/services-congrats-icon';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import { OfficeViewer } from '@components/shared/OfficeViewer';
import { PdfViewer } from '@components/shared/PdfViewer';
import { theme } from '@config/theme';
import { getIcon } from '@features/requests/incomingRequests/incomingRequests';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useTruncate } from '@hooks/useTruncate';
import { useWindowSize } from '@hooks/useWindowSize';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Box, Button, CardMedia, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { isOfficeFile, isPdfFile } from '@utils/fileUtils';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useCrops, useCropsStore } from 'services/crops';
import { useLanguages, useLanguagesStore } from 'services/languages';
import { useServicesByIds, useServicesStore } from 'services/services';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import { useGetCurrentStepData } from '../store/findSpecialistHooks';
import { completeRequest } from '../store/findSpecialistService';
import { useExpressStore } from '../store/findSpecialistStore';
import CircularProgress from '@mui/material/CircularProgress';

const ONE_TIME_DURATION = 5;

export function CongratsStep() {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { pathname } = useLocation();
	const { t } = useTranslation();

	const isFindSpecialistForm = useMemo(() => {
		return !pathname.includes('express');
	}, [pathname]);

	const buildBackRoute = useMemo(() => {
		if (!isFindSpecialistForm) {
			return `/farmer/services/express/search`;
		}

		return `/farmer/services/specialist-request/search`;
	}, [isFindSpecialistForm]);

	const handleRouteToOffers = () => {
		if (isFindSpecialistForm) {
			navigateWithLanguage('/farmer/seasonal-onetime/offers');
		} else {
			navigateWithLanguage('/farmer/express/offers');
		}
	};

	return (
		<div
			className="flex flex-col !justify-center !items-center  xs:mt-0 h-full gap-5 p-5 "
			style={{
				height: 'calc(100vh - 200px)',
			}}
		>
			<div className="flex flex-col items-center justify-center gap-[8px]">
				<h1 className="text-primary flex items-center justify-center gap-[10px] font-roboto text-xl font-bold leading-normal">
					<ServicesCongratsIcon />
					{t('expressSuccessMessages.congratulations')}
				</h1>
				<p className="text-secondary text-center font-roboto text-base font-normal leading-[143.75%]">
					{t('expressSuccessMessages.specialistsHave')}{' '}
					<span className="text-dark font-roboto text-base font-bold leading-[143.75%]">
						3 {t('expressSuccessMessages.days')}
					</span>{' '}
					{t('expressSuccessMessages.toReview')}
				</p>
			</div>
			<Button
				variant="contained"
				onClick={handleRouteToOffers}
				color="primary"
				className="flex items-center justify-center !mt-[50px]"
				sx={{
					padding: '10px 20px',
				}}
			>
				{t('expressSuccessMessages.viewMyOffers')}
			</Button>

			<Button
				variant="text"
				color="primary"
				onClick={() => navigateWithLanguage(buildBackRoute, { replace: true })}
				startIcon={<ArrowBackOutlinedIcon />}
				className="flex items-center justify-center !mt-[50px] !underline"
				sx={{
					padding: '10px 20px',
				}}
			>
				{t('expressSuccessMessages.sendAnotherRequest')}
			</Button>
		</div>
	);
}

export const ReviewAndSendStep = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const [imageIndex, setImageIndex] = useState(-1);
	const { pathname } = useLocation();
	const isFindSpecialistForm = useMemo(() => {
		return !pathname.includes('express');
	}, [pathname]);
	const [mapOpen, setMapOpen] = useState(false);
	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => setMapOpen(false);
	const modalDimensions = useModalDimensions();
	const [showSuccess, setShowSuccess] = useState(false);
	const { fetchServicesByIds } = useServicesByIds();
	const { servicesByIds } = useServicesStore();
	const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
	const { languages } = useLanguagesStore();
	const { crops } = useCropsStore();
	const { requestId: urlRequestId } = useParams();
	const { width } = useWindowSize();
	const isMobile = useMemo(() => width < 768, [width]);
	const { truncate } = useTruncate();

	// PDF Viewer state
	const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
	const [currentPdfUrl, setCurrentPdfUrl] = useState('');
	const [currentPdfFileName, setCurrentPdfFileName] = useState('');

	// Office Viewer state
	const [officeViewerOpen, setOfficeViewerOpen] = useState(false);
	const [currentOfficeUrl, setCurrentOfficeUrl] = useState('');
	const [currentOfficeFileName, setCurrentOfficeFileName] = useState('');

	const buildBackRoute = useMemo(() => {
		if (!isFindSpecialistForm) {
			return `/farmer/services/express/description/${urlRequestId}`;
		}

		return `/farmer/services/specialist-request/description/${urlRequestId}`;
	}, [isFindSpecialistForm, urlRequestId]);

	const { coords } = useGeolocated();
	const initialPosition = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	useLanguages();
	useCrops();

	const { expressData } = useExpressStore();
	const groupFilesByType = useMemo(() => {
		const files = expressData?.request_files || [];

		return {
			images: files?.filter((file: any) => {
				return file.fileType === 'image' || file.fileType === 'video';
			}),
			documents: files?.filter((file: any) => {
				return file.fileType === 'document';
			}),
		};
	}, [expressData]);

	const { t } = useTranslation();

	useGetCurrentStepData(urlRequestId || '');

	const isExpress = useMemo(() => {
		return expressData?.request_type === 'EXPRESS';
	}, [expressData]);

	const userCommunicationLanguage = useMemo(() => {
		return languages.filter((language) =>
			expressData?.communication_language.includes(language.id)
		);
	}, [languages, expressData]);

	const userCrop = useMemo(() => {
		return crops.find((crop) => crop.id === expressData?.crop_id);
	}, [crops, expressData]);

	const markerPosition = useMemo(
		() => ({
			lng: Number(expressData?.longitude ?? initialPosition.lng),
			lat: Number(expressData?.latitude ?? initialPosition.lat),
		}),
		[expressData?.longitude, expressData?.latitude, initialPosition]
	);

	// Add a state for tracking submission status
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSendRequest = async () => {
		if (isSubmitting) return; // Prevent multiple submissions
		
		try {
			setIsSubmitting(true); // Set loading state to true
			const response = await completeRequest(urlRequestId || '', true);

			if (response.success) {
				setShowSuccess(true);
				localStorage.removeItem('requestId');
			}
		} catch (error) {
			console.error('Error sending request:', error);
			// You could add error handling here if needed
		} finally {
			setIsSubmitting(false); // Reset loading state
		}
	};

	const fetchedRef = useRef(false);

	useEffect(() => {
		if (expressData?.services_id?.length > 0 && !fetchedRef.current) {
			fetchServicesByIds(expressData.services_id.map((service: string) => service));
			fetchedRef.current = true;
		}
	}, [expressData?.services_id, fetchServicesByIds]);

	return showSuccess ? (
		<CongratsStep />
	) : (
		<div className="pb-0 sm:pb-5 h-auto p-5  sm:h-full flex flex-col gap-5 mt-5">
			<Box className="flex flex-col  items-center justify-center border-b border-[#E9ECEF]">
				<Typography
					variant="h1"
					textAlign="center"
					mt={4}
					mb={2}
					className="text-dark font-roboto text-xl font-bold leading-normal"
				>
					{t('reviewRequestDetails')}
				</Typography>
			</Box>

			<Box className=" h-auto  sm:h-full flex flex-col gap-5 pb-5 ">
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-b border-[#E9ECEF] pb-5">
					<div className="flex gap-[22px] items-center sm:relative sm:after:content-[''] sm:after:absolute sm:after:right-[-11px] sm:after:top-1/2 sm:after:-translate-y-1/2 sm:after:w-[1px] sm:after:h-1/2 sm:after:bg-light border-b sm:border-b-0 border-[#E9ECEF] pb-5 sm:pb-0">
						<div className="relative">
							<CardMedia
								component="img"
								image={userCrop?.image_url}
								alt="Blueberry"
								sx={{
									width: 74,
									height: 74,
									borderRadius: '12px',
									boxShadow: theme.boxShadows.primary,
								}}
							/>
						</div>
						<div className="flex flex-col gap-[10px]">
							<h4 className="text-dark font-roboto text-xl font-bold leading-normal">
								{userCrop?.name}
							</h4>
						</div>
					</div>
					<div className="flex flex-col justify-center items-start sm:items-center sm:relative sm:after:content-[''] sm:after:absolute sm:after:right-[-11px] sm:after:top-1/2 sm:after:-translate-y-1/2 sm:after:w-[1px] sm:after:h-1/2 sm:after:bg-light border-b sm:border-b-0 border-[#E9ECEF] pb-5 sm:pb-0">
						{isExpress ? (
							<div className="flex items-center gap-5">
								<div className="p-[10px] bg-disabled rounded-[8px]">
									<PaymentIcon sx={{ color: '#7B809A' }} />
								</div>
								<div className="flex flex-col">
									<h4 className="text-dark font-roboto text-base font-bold">
										€{servicesByIds.reduce((sum, service) => sum + (service.price || 0), 0)}
									</h4>
									<h6 className="text-secondary font-roboto text-base font-normal">
										{t('totalPriceText')}
									</h6>
								</div>
							</div>
						) : (
							<h6 className="text-dark font-roboto text-base font-bold leading-normal">
								{expressData?.request_type === 'ONE_TIME'
									? t('findSpecialistPage.oneTimeConsultation')
									: t('findSpecialistPage.seasonalConsultation')}
							</h6>
						)}

						{expressData?.request_type === 'ONE_TIME' && (
							<p className="text-secondary font-roboto text-sm font-normal leading-normal">
								{t('oneTimeDuration', { duration: ONE_TIME_DURATION })}
							</p>
						)}
					</div>
					<div className="flex gap-[22px] items-center">
						<div className="rounded-[8px] bg-light-grey ">
							<DateRangeIcon sx={{ color: theme.palette.custom.gray }} />
						</div>
						<div className="flex flex-col gap-[10px]">
							<h6 className="text-dark font-roboto text-base font-bold leading-normal">
								{expressData?.start_date
									? format(parseISO(expressData.start_date), 'dd.MM.yyyy')
									: ''}
							</h6>
							<p className="text-secondary font-roboto text-sm font-normal leading-normal">
								{t('requestValidForDays', { days: 3 })}
							</p>
						</div>
					</div>
				</div>

				{isExpress && (
					<Box className="border-b border-[#E9ECEF] pb-5 mb-5">
						<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
							{t('service')}
						</h6>
						<Grid container spacing={2}>
							{servicesByIds.map((service) => (
								<Grid size={{ xs: 12, sm: 6, md: 3 }} key={service.id}>
									<div className="flex gap-[10px] overflow-hidden">
										<div className="rounded-[10px] border border-dark flex justify-between items-center w-full">
											<div className="flex flex-col p-[10px] gap-3">
												<p className="text-dark font-roboto text-base font-bold">{service.name}</p>
												<p className="text-secondary italic font-normal font-roboto text-sm">
													{t('oneTimeDuration', { duration: (service as any).duration })}
												</p>
											</div>
											<div className="flex items-center justify-center h-full">
												<div className="flex items-center justify-center text-black italic font-roboto border-l border-dark text-base font-bold h-full bg-disabled px-[16px] py-5 rounded-tr-[10px] rounded-br-[10px]">
													€{service.price}
												</div>
											</div>
										</div>
									</div>
								</Grid>
							))}
						</Grid>
					</Box>
				)}

				<Box>
					<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
						{t('farmDetails')}
					</h6>
					<div className="flex gap-[10px] items-center  border-r border-dark">
						<FarmIcon />
						<p className="text-secondary font-roboto text-sm font-normal leading-normal">
							{expressData?.area} {expressData?.unit}
						</p>
					</div>
					<div className="flex gap-[10px] items-center mt-[25px] border-b border-[#E9ECEF] pb-5 mb-5">
						<FmdGoodOutlinedIcon sx={{ color: theme.palette.custom.gray }} />
						<p className="text-secondary font-roboto text-sm font-normal leading-normal">
							{truncate(expressData?.location_name || 'N/A', isMobile ? 20 : 200)}
						</p>
						<div className="rounded-[12px] bg-light-grey">
							<a
								onClick={openMapsModal}
								className="text-primary  cursor-pointer text-right font-roboto text-sm font-bold leading-normal underline decoration-solid"
							>
								{t('viewOnMap')}
							</a>
						</div>
					</div>
				</Box>

				{expressData?.request_type === 'SEASONAL' && (
					<Box className=" border-b border-[#E9ECEF] pb-5 mb-5">
						<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
							{t('consultationPeriod')}
						</h6>
						<div className="flex  gap-[10px]">
							<DateRangeIcon sx={{ color: theme.palette.custom.gray }} />
							<div className="flex flex-col gap-[4px]">
								<p className="text-secondary font-roboto text-sm font-normal leading-normal">
									{expressData?.start_date
										? format(parseISO(expressData.start_date), 'dd.MM.yyyy')
										: ''}{' '}
									{expressData?.start_date && expressData?.end_date
										? `- ${format(parseISO(expressData.end_date), 'dd.MM.yyyy')}`
										: ''}
								</p>
								<p className="text-secondary font-roboto text-xs font-normal text-center">
									{t('requestValidForDays', { days: 3 })}
								</p>
							</div>
						</div>
					</Box>
				)}

				<Box className=" border-b border-[#E9ECEF] pb-5 mb-5">
					<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
						{t('communicationLanguage')}
					</h6>
					<div className="flex items-center gap-[10px]">
						<RecordVoiceOverOutlinedIcon sx={{ color: theme.palette.custom.gray }} />

						{userCommunicationLanguage.map((language) => (
							<Chip
								key={language.id}
								label={
									<div className="flex gap-[10px]">
										<p>{language.name}</p>
									</div>
								}
								variant="outlined"
								sx={{
									borderRadius: '8px',
									border: 0,
									backgroundColor: '#F8F9FA',
									padding: '1px 10px',
									color: '#7B809A',
									fontFamily: 'Roboto',
									fontSize: '16px',
									fontStyle: 'normal',
									fontWeight: 400,
									lineHeight: '140%',
									height: '24px',
									textTransform: 'camelcase',
									'& .MuiChip-label': {
										padding: 0,
									},
								}}
							/>
						))}
					</div>
				</Box>
				<Box className=" border-b border-[#E9ECEF] pb-5 mb-5">
					<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
						{t('description')}
					</h6>
					<p
						className="text-secondary font-roboto text-base font-normal leading-normal"
						dangerouslySetInnerHTML={{ __html: expressData?.problem_description || 'N/A' }}
					></p>
				</Box>

				<Box className="pb-5 mb-5">
					{groupFilesByType?.documents?.length ? (
						<div className="border-b border-[#E9ECEF] pb-5 ">
							<h6 className="text-dark font-roboto text-base font-bold leading-normal mb-[10px]">
								{t('attachments')}
							</h6>

							<div className="flex flex-col gap-[10px]">
								{groupFilesByType.documents.map((file: any, index: any) => (
									<div
										className="flex gap-[10px] cursor-pointer"
										key={index}
										onClick={() => {
											const fileUrl = `${process.env.REACT_APP_CDN_URL}${file.s3_key}`;

											if (isPdfFile(file.filename)) {
												setCurrentPdfUrl(fileUrl);
												setCurrentPdfFileName(file.filename);
												setPdfViewerOpen(true);
											} else if (isOfficeFile(file.filename)) {
												setCurrentOfficeUrl(fileUrl);
												setCurrentOfficeFileName(file.filename);
												setOfficeViewerOpen(true);
											} else {
												window.open(fileUrl, '_blank');
											}
										}}
									>
										<img src={getIcon(file.fileType as any)} alt={file.filename} />
										<span className="text-secondary roboto-regular text-[16px] leading-normal underline">
											{file.filename}
										</span>
									</div>
								))}
							</div>
						</div>
					) : null}

					{groupFilesByType?.images?.length ? (
						<div className="flex flex-wrap gap-[10px] mt-[10px]">
							{groupFilesByType.images.map((resource: any, index: any) => {
								const isVideo = resource.fileType === 'video';
								const thumbnailUrl = `${process.env.REACT_APP_CDN_URL}${resource.s3_key}`;

								return (
									<div
										className="group relative w-[77px] h-[77px] rounded-[12px] shadow-custom-button cursor-pointer"
										key={index}
										onClick={() => setImageIndex(index)}
									>
										{isVideo ? (
											<>
												<div className="w-full h-full rounded-[12px] bg-gray-200 flex items-center justify-center">
													<PlayArrowIcon className="text-gray-500" style={{ fontSize: 36 }} />
												</div>
												<div className="absolute inset-0 bg-[rgba(3,136,119,0.7)] rounded-[12px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
													<PlayArrowIcon className="text-white" style={{ fontSize: 36 }} />
												</div>
											</>
										) : (
											<>
												<img
													className="w-full h-full rounded-[12px] object-cover"
													src={thumbnailUrl}
													alt={`resource-${index}`}
												/>
												<div className="absolute inset-0 bg-[rgba(3,136,119,0.7)] rounded-[12px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
													<ZoomInIcon className="text-white text-[36px]" />
												</div>
											</>
										)}
									</div>
								);
							})}
						</div>
					) : null}
				</Box>
			</Box>
			<Lightbox
				index={imageIndex}
				slides={groupFilesByType.images.map((file: any) => {
					const isVideo = file.fileType === 'video';
					const url = `${process.env.REACT_APP_CDN_URL}${file.s3_key}`;

					return isVideo
						? {
								type: 'video',
								width: 1280,
								height: 720,
								poster: url,
								sources: [
									{
										src: url,
										type: 'video/mp4',
									},
								],
							}
						: {
								src: url,
								description: file.filename,
							};
				})}
				open={imageIndex >= 0}
				close={() => setImageIndex(-1)}
				plugins={[Video]}
			/>

			<div className="flex flex-col gap-[10px] md:flex-row md:justify-between pb-5">
				<Button
					startIcon={<ArrowBackIosNewOutlinedIcon />}
					variant="contained"
					onClick={() => navigateWithLanguage(buildBackRoute)}
					sx={{ backgroundColor: '#fff', color: theme.palette.custom.darkGray }}
				>
					{t('previous')}
				</Button>
				<Button
					onClick={handleSendRequest}
					endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendOutlinedIcon />}
					variant="contained"
					color="primary"
					disabled={isSubmitting}
					sx={{
						"&.Mui-disabled": {
							backgroundColor: theme.palette.primary.main,
							color: "#fff",
							opacity: 0.7
						}
					}}
				>
					{isSubmitting ? t('chats.sending', { defaultValue: 'Sending...' }) : t('review-and-send')}
				</Button>
			</div>

			<GeneralMuiModal
				maxWidth={'xl'}
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },

					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<TaMaps
						disabled={true}
						marker={markerPosition}
						currentAddress={expressData?.location_name}
					/>
				}
			/>

			<PdfViewer
				open={pdfViewerOpen}
				onClose={() => setPdfViewerOpen(false)}
				pdfUrl={currentPdfUrl}
				fileName={currentPdfFileName}
			/>

			{/* Office Viewer component for viewing Office documents */}
			<OfficeViewer
				open={officeViewerOpen}
				onClose={() => setOfficeViewerOpen(false)}
				documentUrl={currentOfficeUrl}
				fileName={currentOfficeFileName}
			/>
		</div>
	);
};
